import { useState } from "react";

/**
 * 取り消せない操作のボタン。
 *
 * ★ **1 クリックでは起こさない。** 押すと「本当に〜」に変わり、もう一度
 * 押して初めて実行する。取り消せない操作（API キーの失効、Credential の
 * 削除）が押した瞬間に走っていて、しかも巻き添えが大きい——Credential を
 * 消すと、それを指している Environment の Run が全部止まる。
 *
 * ★ **走っている間は押せない。** 押しても何も変わらないと、人は
 * **もう一度押す**。2 回走ると、2 つ目は「無い」に当たって謎の失敗になる。
 *
 * ★ **形は 1 つに寄せる。** バンドルの一覧だけがこの 2 段を持っていて、
 * 他の画面は 1 クリックだった。画面ごとに作法が違うと、どこで確かめが
 * 出るのか覚えられない。
 */

export interface DangerButtonProps {
  /** 押す前の札（「削除」「失効」など）。 */
  readonly label: string;
  /**
   * 実行する。
   *
   * ★ **約束を返すことを型で要求する。** `void` を返せる形にしていたら、
   * 呼ぶ側が `void client…` と書けてしまい、**待たずに終わったことに
   * なる**（走っている間が無くなり、連打できる）。変異で生き延びたので、
   * テストを足すのではなく型で塞いだ。
   */
  readonly onConfirm: () => Promise<unknown>;
  /** 外から止めるとき（読み込み中など）。 */
  readonly disabled?: boolean;
}

export function DangerButton({
  label,
  onConfirm,
  disabled = false,
}: DangerButtonProps): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!confirming) {
    return (
      <button
        type="button"
        className="danger"
        disabled={disabled}
        onClick={() => setConfirming(true)}
      >
        {label}
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        className="danger"
        disabled={disabled || busy}
        onClick={() => {
          setBusy(true);
          void onConfirm().finally(() => {
            setBusy(false);
            setConfirming(false);
          });
        }}
      >
        {/* ★ 何をするのかを札に残す。「本当に？」だけでは、何が消えるのか
            分からないまま押すことになる。 */}
        {busy ? `${label}しています…` : `本当に${label}`}
      </button>
      {/* ★ やめる道を必ず出す。走っている間は消す——押しても
          止まらないものを出すと、止められると思わせる。 */}
      {!busy && (
        <button type="button" onClick={() => setConfirming(false)}>
          やめる
        </button>
      )}
    </>
  );
}
